var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);

// src/components/content/ActiveFilterBadges.tsx
import { Filter, X } from "lucide-react";
import { jsx, jsxs } from "react/jsx-runtime";
var accentColors = {
  violet: {
    icon: "text-violet-400",
    badge: "bg-violet-500 hover:bg-violet-600"
  },
  emerald: {
    icon: "text-emerald-400",
    badge: "bg-emerald-500 hover:bg-emerald-600"
  },
  blue: {
    icon: "text-blue-400",
    badge: "bg-blue-500 hover:bg-blue-600"
  },
  amber: {
    icon: "text-amber-400",
    badge: "bg-amber-500 hover:bg-amber-600"
  },
  rose: {
    icon: "text-rose-400",
    badge: "bg-rose-500 hover:bg-rose-600"
  }
};
function ActiveFilterBadges({
  filters,
  resultCount,
  resultLabel = "items",
  accentColor = "violet",
  onClearAll,
  emptyMessage = "No filters applied",
  className = ""
}) {
  const colors = accentColors[accentColor];
  const hasFilters = filters.length > 0;
  if (!hasFilters) {
    return /* @__PURE__ */ jsxs("div", { className: `flex items-center gap-2 ${className}`, children: [
      /* @__PURE__ */ jsx("span", { className: "text-sm text-text-muted", children: emptyMessage }),
      resultCount !== void 0 && /* @__PURE__ */ jsxs("span", { className: "text-sm text-text-muted", children: [
        "\u2022 ",
        resultCount,
        " ",
        resultCount === 1 ? resultLabel.replace(/s$/, "") : resultLabel
      ] })
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { className: `flex items-center gap-2 flex-wrap ${className}`, children: [
    /* @__PURE__ */ jsx(Filter, { size: 16, className: colors.icon }),
    /* @__PURE__ */ jsx("span", { className: "text-sm text-text-muted", children: "Filtered by:" }),
    filters.map((filter) => /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: filter.onRemove,
        className: `
            px-3 py-1
            ${colors.badge}
            text-white text-sm rounded-full
            transition-colors
            inline-flex items-center gap-1.5
            hover:shadow-md
          `,
        "aria-label": `Remove filter: ${filter.label || ""} ${filter.value}`,
        children: [
          filter.label && /* @__PURE__ */ jsxs("span", { className: "font-medium", children: [
            filter.label,
            ":"
          ] }),
          /* @__PURE__ */ jsx("span", { children: filter.value }),
          /* @__PURE__ */ jsx(X, { size: 14, className: "opacity-80" })
        ]
      },
      filter.id
    )),
    resultCount !== void 0 && /* @__PURE__ */ jsxs("span", { className: "text-sm text-text-muted", children: [
      "(",
      resultCount,
      " ",
      resultCount === 1 ? resultLabel.replace(/s$/, "") : resultLabel,
      ")"
    ] }),
    onClearAll && filters.length > 1 && /* @__PURE__ */ jsx(
      "button",
      {
        onClick: onClearAll,
        className: "text-sm text-text-muted hover:text-text-primary underline ml-2",
        "aria-label": "Clear all filters",
        children: "Clear all"
      }
    )
  ] });
}

// src/components/content/ContentSearch.tsx
import Fuse from "fuse.js";
import { Search, X as X2 } from "lucide-react";
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var accentColors2 = {
  violet: {
    border: "focus:border-violet-500",
    ring: "focus:ring-violet-500/20",
    text: "text-violet-400",
    bg: "bg-violet-500/10"
  },
  emerald: {
    border: "focus:border-emerald-500",
    ring: "focus:ring-emerald-500/20",
    text: "text-emerald-400",
    bg: "bg-emerald-500/10"
  },
  blue: {
    border: "focus:border-blue-500",
    ring: "focus:ring-blue-500/20",
    text: "text-blue-400",
    bg: "bg-blue-500/10"
  },
  amber: {
    border: "focus:border-amber-500",
    ring: "focus:ring-amber-500/20",
    text: "text-amber-400",
    bg: "bg-amber-500/10"
  },
  rose: {
    border: "focus:border-rose-500",
    ring: "focus:ring-rose-500/20",
    text: "text-rose-400",
    bg: "bg-rose-500/10"
  }
};
function ContentSearch({
  items,
  onSearch,
  onQueryChange,
  searchFields = ["title", "excerpt"],
  fieldWeights,
  placeholder = "Search...",
  accentColor = "violet",
  showResultCount = true,
  showIcon = false,
  debounceMs = 300,
  className = "",
  fuzzySearch = true,
  threshold = 0.4
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const justClearedRef = useRef(false);
  const colors = accentColors2[accentColor];
  const fuseKeys = useMemo(() => {
    if (fieldWeights && fieldWeights.length > 0) {
      return fieldWeights.map((fw) => ({
        name: fw.name,
        weight: fw.weight ?? 1
      }));
    }
    return searchFields.map((field) => ({
      name: field,
      weight: 1
    }));
  }, [fieldWeights, searchFields]);
  const fuse = useMemo(() => {
    if (!fuzzySearch) return null;
    return new Fuse(items, {
      keys: fuseKeys,
      threshold,
      includeScore: true,
      ignoreLocation: true,
      // Search entire string, not just beginning
      useExtendedSearch: false,
      findAllMatches: true
    });
  }, [items, fuseKeys, threshold, fuzzySearch]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
      onQueryChange?.(query.trim());
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [query, debounceMs, onQueryChange]);
  const deferredQuery = useDeferredValue(debouncedQuery);
  const normalizedQuery = useMemo(() => deferredQuery.trim(), [deferredQuery]);
  const searchTerms = useMemo(
    () => normalizedQuery.toLowerCase().split(" ").filter(Boolean),
    [normalizedQuery]
  );
  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((current, key) => {
      if (current && typeof current === "object" && key in current) {
        return current[key];
      }
      return void 0;
    }, obj);
  };
  const filteredItems = useMemo(() => {
    if (!normalizedQuery) {
      return items;
    }
    if (fuzzySearch && fuse) {
      const results = fuse.search(normalizedQuery);
      return results.map((result) => result.item);
    }
    return items.filter((item) => {
      const searchableText = searchFields.map((field) => {
        const value = getNestedValue(item, field);
        if (Array.isArray(value)) {
          return value.join(" ");
        }
        if (typeof value === "object" && value !== null) {
          return "";
        }
        if (value === void 0 || value === null) {
          return "";
        }
        return String(value);
      }).join(" ").toLowerCase();
      return searchTerms.every((term) => searchableText.includes(term));
    });
  }, [items, normalizedQuery, searchTerms, searchFields, fuzzySearch, fuse]);
  useEffect(() => {
    if (justClearedRef.current) {
      if (normalizedQuery === "") {
        justClearedRef.current = false;
      }
      return;
    }
    onSearch(filteredItems, { query: normalizedQuery, terms: searchTerms });
  }, [filteredItems, normalizedQuery, onSearch, searchTerms]);
  const handleClear = () => {
    justClearedRef.current = true;
    setQuery("");
    setDebouncedQuery("");
    onSearch(items, { query: "", terms: [] });
  };
  return /* @__PURE__ */ jsxs2("div", { className: `space-y-3 ${className}`, children: [
    /* @__PURE__ */ jsx2("div", { className: "relative", children: /* @__PURE__ */ jsxs2("div", { className: "relative flex items-center", children: [
      showIcon && /* @__PURE__ */ jsx2(Search, { size: 20, className: `absolute left-4 ${colors.text} pointer-events-none` }),
      /* @__PURE__ */ jsx2(
        "input",
        {
          type: "text",
          value: query,
          onChange: (e) => setQuery(e.target.value),
          placeholder,
          className: `
              w-full ${showIcon ? "pl-12" : "pl-4"} pr-12 py-3
              bg-surface-raised backdrop-blur-sm
              border border-surface-border rounded-lg
              text-text-primary placeholder-text-muted
              transition-all duration-200
              ${colors.border} ${colors.ring}
              focus:outline-none focus:ring-2
            `
        }
      ),
      query && /* @__PURE__ */ jsx2(
        "button",
        {
          onClick: handleClear,
          className: "absolute right-4 p-1 hover:bg-surface-hover rounded transition-colors",
          "aria-label": "Clear search",
          children: /* @__PURE__ */ jsx2(X2, { size: 18, className: "text-text-muted hover:text-text-primary" })
        }
      )
    ] }) }),
    showResultCount && debouncedQuery && /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-2 text-sm", children: [
      /* @__PURE__ */ jsxs2("span", { className: "text-text-muted", children: [
        "Found ",
        filteredItems.length,
        " ",
        filteredItems.length === 1 ? "result" : "results"
      ] }),
      filteredItems.length > 0 && /* @__PURE__ */ jsx2("span", { className: "text-text-muted opacity-50", children: "\u2022" }),
      filteredItems.length > 0 && /* @__PURE__ */ jsxs2("span", { className: `${colors.text} font-medium`, children: [
        '"',
        debouncedQuery,
        '"'
      ] })
    ] }),
    showResultCount && debouncedQuery && filteredItems.length === 0 && /* @__PURE__ */ jsxs2("div", { className: `p-4 rounded-lg ${colors.bg} border border-surface-border`, children: [
      /* @__PURE__ */ jsxs2("p", { className: "text-sm text-text-muted text-center", children: [
        "No results found for",
        " ",
        /* @__PURE__ */ jsxs2("span", { className: `${colors.text} font-medium`, children: [
          '"',
          debouncedQuery,
          '"'
        ] })
      ] }),
      /* @__PURE__ */ jsxs2("p", { className: "text-xs text-text-muted opacity-75 text-center mt-1", children: [
        "Try different keywords or",
        " ",
        /* @__PURE__ */ jsx2("button", { onClick: handleClear, className: `${colors.text} hover:underline`, children: "clear search" })
      ] })
    ] })
  ] });
}

// src/components/content/ContentSort.tsx
import { ArrowUpDown } from "lucide-react";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var accentColors3 = {
  violet: "focus:border-violet-500 focus:ring-violet-500/20",
  emerald: "focus:border-emerald-500 focus:ring-emerald-500/20",
  blue: "focus:border-blue-500 focus:ring-blue-500/20",
  amber: "focus:border-amber-500 focus:ring-amber-500/20",
  rose: "focus:border-rose-500 focus:ring-rose-500/20"
};
function ContentSort({
  value,
  onChange,
  options,
  accentColor = "violet",
  label = "Sort by",
  showIcon = true,
  className = ""
}) {
  const colors = accentColors3[accentColor];
  return /* @__PURE__ */ jsxs3("div", { className: `flex items-center gap-2 ${className}`, children: [
    showIcon && /* @__PURE__ */ jsx3(ArrowUpDown, { size: 16, className: "text-text-muted" }),
    /* @__PURE__ */ jsx3("label", { htmlFor: "content-sort", className: "text-sm text-text-muted whitespace-nowrap", children: label }),
    /* @__PURE__ */ jsx3(
      "select",
      {
        id: "content-sort",
        value,
        onChange: (e) => onChange(e.target.value),
        className: `
          px-3 py-2 pr-10
          bg-surface-base/50 backdrop-blur-sm
          border border-surface-border rounded-lg
          text-text-primary text-sm
          transition-all duration-200
          ${colors}
          focus:outline-none focus:ring-2
          cursor-pointer
          hover:border-surface-hover
        `,
        children: options.map((option) => /* @__PURE__ */ jsx3("option", { value: option.value, children: option.label }, option.value))
      }
    )
  ] });
}

// src/components/content/LoadMoreButton.tsx
import { ChevronDown } from "lucide-react";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var accentColors4 = {
  violet: {
    primary: "bg-violet-600 hover:bg-violet-700",
    secondary: "bg-surface-raised hover:bg-surface-hover border border-violet-500/30"
  },
  emerald: {
    primary: "bg-emerald-600 hover:bg-emerald-700",
    secondary: "bg-surface-raised hover:bg-surface-hover border border-emerald-500/30"
  },
  blue: {
    primary: "bg-blue-600 hover:bg-blue-700",
    secondary: "bg-surface-raised hover:bg-surface-hover border border-blue-500/30"
  },
  amber: {
    primary: "bg-amber-600 hover:bg-amber-700",
    secondary: "bg-surface-raised hover:bg-surface-hover border border-amber-500/30"
  },
  rose: {
    primary: "bg-rose-600 hover:bg-rose-700",
    secondary: "bg-surface-raised hover:bg-surface-hover border border-rose-500/30"
  }
};
function LoadMoreButton({
  remainingCount,
  itemsPerLoad,
  onLoadMore,
  itemLabel = "items",
  accentColor = "violet",
  variant = "primary",
  className = ""
}) {
  if (remainingCount <= 0) return null;
  const colors = accentColors4[accentColor][variant];
  const loadCount = Math.min(remainingCount, itemsPerLoad);
  const singularLabel = itemLabel.replace(/s$/, "");
  const displayLabel = loadCount === 1 ? singularLabel : itemLabel;
  return /* @__PURE__ */ jsxs4("div", { className: `text-center ${className}`, children: [
    /* @__PURE__ */ jsxs4(
      "button",
      {
        onClick: onLoadMore,
        className: `
          inline-flex items-center gap-2
          px-6 py-3
          ${colors}
          text-white font-semibold rounded-lg
          transition-colors
          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-interactive-focus
        `,
        "aria-label": `Load ${loadCount} more ${displayLabel}`,
        children: [
          "Load ",
          loadCount,
          " More ",
          displayLabel.charAt(0).toUpperCase() + displayLabel.slice(1),
          /* @__PURE__ */ jsx4(ChevronDown, { size: 20 })
        ]
      }
    ),
    /* @__PURE__ */ jsxs4("p", { className: "text-sm text-text-muted mt-2", children: [
      remainingCount,
      " remaining"
    ] })
  ] });
}

// src/components/content/SiteSearch.tsx
import { Search as SearchIcon, X as X3 } from "lucide-react";
import { useCallback, useEffect as useEffect2, useRef as useRef2, useState as useState2 } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Fragment, jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var defaultTypeColors = {
  violet: {
    default: "bg-violet-500/20 text-violet-400"
  },
  blue: {
    default: "bg-blue-500/20 text-blue-400"
  },
  sage: {
    default: "bg-brand-accent/20 text-brand-accent"
  },
  default: {
    default: "bg-text-muted/20 text-text-muted"
  }
};
function SiteSearch({
  searchableContent,
  placeholder = "Search...",
  variant = "violet",
  typeColors,
  maxResults = 8
}) {
  const [isOpen, setIsOpen] = useState2(false);
  const [query, setQuery] = useState2("");
  const [results, setResults] = useState2([]);
  const [selectedIndex, setSelectedIndex] = useState2(-1);
  const inputRef = useRef2(null);
  const resultsRef = useRef2(null);
  const navigate = useNavigate();
  useEffect2(() => {
    const handleKeyDown2 = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown2);
    return () => document.removeEventListener("keydown", handleKeyDown2);
  }, []);
  useEffect2(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  useEffect2(() => {
    if (!query.trim()) {
      setResults([]);
      setSelectedIndex(-1);
      return;
    }
    const searchTerms = query.toLowerCase().split(" ");
    const filtered = searchableContent.filter((item) => {
      const searchText = `${item.title} ${item.excerpt}`.toLowerCase();
      return searchTerms.every((term) => searchText.includes(term));
    });
    setResults(filtered.slice(0, maxResults));
    setSelectedIndex(filtered.length > 0 ? 0 : -1);
  }, [query, searchableContent, maxResults]);
  const handleResultClick = useCallback(
    (path) => {
      void navigate(path);
      setIsOpen(false);
      setQuery("");
      setSelectedIndex(-1);
    },
    [navigate]
  );
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => prev < results.length - 1 ? prev + 1 : prev);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => prev > 0 ? prev - 1 : 0);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleResultClick(results[selectedIndex].path);
        } else if (results.length > 0) {
          handleResultClick(results[0].path);
        }
      }
    },
    [results, selectedIndex, handleResultClick]
  );
  useEffect2(() => {
    if (selectedIndex >= 0 && resultsRef.current) {
      const selectedElement = resultsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex]);
  const getTypeColor = (type) => {
    if (typeColors && typeColors[type]) {
      return typeColors[type];
    }
    return defaultTypeColors[variant]?.default || defaultTypeColors.default.default;
  };
  return /* @__PURE__ */ jsxs5(Fragment, { children: [
    /* @__PURE__ */ jsxs5(
      "button",
      {
        onClick: () => setIsOpen(true),
        className: "flex items-center gap-2 px-3 py-2 rounded-lg bg-interactive-default/10 hover:bg-interactive-default/20 transition-colors text-interactive-default hover:text-interactive-hover border border-interactive-default/30",
        title: "Search (Ctrl+K)",
        children: [
          /* @__PURE__ */ jsx5(SearchIcon, { size: 18 }),
          /* @__PURE__ */ jsx5("span", { className: "hidden sm:inline text-sm font-medium", children: "Search" }),
          /* @__PURE__ */ jsx5("kbd", { className: "hidden sm:inline px-2 py-0.5 text-xs bg-surface-base/50 rounded border border-interactive-default/30", children: "\u2318K" })
        ]
      }
    ),
    isOpen && createPortal(
      /* @__PURE__ */ jsxs5(Fragment, { children: [
        /* @__PURE__ */ jsx5(
          "div",
          {
            className: "fixed inset-0 bg-black/70 backdrop-blur-sm z-50",
            onClick: () => setIsOpen(false),
            onKeyDown: (e) => e.key === "Escape" && setIsOpen(false),
            role: "presentation",
            "aria-hidden": "true"
          }
        ),
        /* @__PURE__ */ jsx5("div", { className: "fixed inset-x-4 top-20 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-full sm:max-w-2xl z-50", children: /* @__PURE__ */ jsxs5("div", { className: "rounded-xl shadow-2xl border-2 border-surface-border overflow-hidden bg-surface-base text-text-primary", children: [
          /* @__PURE__ */ jsxs5("div", { className: "flex items-center gap-3 p-4 border-b-2 border-surface-border bg-surface-raised", children: [
            /* @__PURE__ */ jsx5(SearchIcon, { size: 20, className: "text-brand-primary flex-shrink-0" }),
            /* @__PURE__ */ jsx5(
              "input",
              {
                ref: inputRef,
                type: "text",
                value: query,
                onChange: (e) => setQuery(e.target.value),
                onKeyDown: handleKeyDown,
                placeholder,
                className: "flex-1 bg-transparent border-none outline-none text-text-primary placeholder:text-text-muted font-medium",
                "aria-autocomplete": "list",
                "aria-controls": "search-results",
                "aria-activedescendant": selectedIndex >= 0 ? `search-result-${selectedIndex}` : void 0
              }
            ),
            /* @__PURE__ */ jsx5(
              "button",
              {
                onClick: () => setIsOpen(false),
                className: "p-1 rounded transition-colors text-text-muted hover:text-brand-primary hover:bg-surface-hover flex-shrink-0",
                "aria-label": "Close search",
                children: /* @__PURE__ */ jsx5(X3, { size: 18 })
              }
            )
          ] }),
          results.length > 0 ? /* @__PURE__ */ jsx5(
            "div",
            {
              ref: resultsRef,
              id: "search-results",
              role: "listbox",
              className: "max-h-96 overflow-y-auto bg-surface-base",
              children: results.map((result, index) => /* @__PURE__ */ jsx5(
                "button",
                {
                  id: `search-result-${index}`,
                  role: "option",
                  "aria-selected": index === selectedIndex,
                  onClick: () => handleResultClick(result.path),
                  onMouseEnter: () => setSelectedIndex(index),
                  className: `w-full text-left p-4 transition-colors border-b last:border-0 border-surface-border text-text-primary ${index === selectedIndex ? "bg-surface-raised border-brand-primary/30" : "hover:bg-surface-raised hover:border-brand-primary/30"}`,
                  children: /* @__PURE__ */ jsxs5("div", { className: "flex items-start gap-3", children: [
                    /* @__PURE__ */ jsx5(
                      "span",
                      {
                        className: `px-2 py-0.5 text-xs rounded font-semibold flex-shrink-0 ${getTypeColor(result.type)}`,
                        children: result.type
                      }
                    ),
                    /* @__PURE__ */ jsxs5("div", { className: "flex-1 min-w-0", children: [
                      /* @__PURE__ */ jsx5("div", { className: "font-semibold mb-1 text-text-primary", children: result.title }),
                      /* @__PURE__ */ jsx5("div", { className: "text-sm line-clamp-2 text-text-muted", children: result.excerpt })
                    ] })
                  ] })
                },
                index
              ))
            }
          ) : query.trim() ? /* @__PURE__ */ jsx5("div", { className: "p-8 text-center text-text-muted bg-surface-base", children: /* @__PURE__ */ jsxs5("p", { className: "font-medium", children: [
            'No results found for "',
            query,
            '"'
          ] }) }) : /* @__PURE__ */ jsx5("div", { className: "p-8 text-center text-text-muted bg-surface-base", children: /* @__PURE__ */ jsx5("p", { className: "font-medium", children: "Start typing to search..." }) })
        ] }) })
      ] }),
      document.body
    )
  ] });
}

// src/components/display/AggregateRating.tsx
import { useCallback as useCallback2, useEffect as useEffect3, useState as useState3 } from "react";
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var sizeClasses = {
  sm: "w-4 h-4",
  md: "w-6 h-6",
  lg: "w-8 h-8"
};
var textSizeClasses = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg"
};
var starColorClasses = {
  "yellow-400": { ring: "focus:ring-yellow-400/60", hex: "#facc15" },
  "violet-400": { ring: "focus:ring-violet-400/60", hex: "#c084fc" },
  "blue-400": { ring: "focus:ring-blue-400/60", hex: "#38bdf8" },
  "emerald-400": { ring: "focus:ring-emerald-400/60", hex: "#34d399" }
};
var AggregateRating = ({
  itemId,
  itemType,
  ratingAPI,
  starColor = "yellow-400",
  onRate,
  size = "md"
}) => {
  const [stats, setStats] = useState3(null);
  const [userRating, setUserRating] = useState3(null);
  const [hoveredStar, setHoveredStar] = useState3(null);
  const [loading, setLoading] = useState3(true);
  const [submitting, setSubmitting] = useState3(false);
  const [statusMessage, setStatusMessage] = useState3(null);
  const [errorMessage, setErrorMessage] = useState3(null);
  const [fetchError, setFetchError] = useState3(null);
  const refreshStats = useCallback2(async () => {
    setFetchError(null);
    try {
      const [statsData, userRatingData] = await Promise.all([
        ratingAPI.getRatingStats(itemId, itemType),
        ratingAPI.getUserRating(itemId, itemType)
      ]);
      setStats(statsData);
      setUserRating(userRatingData);
    } catch {
      setFetchError("Unable to load ratings");
    }
  }, [itemId, itemType, ratingAPI]);
  useEffect3(() => {
    setLoading(true);
    void refreshStats().finally(() => setLoading(false));
  }, [refreshStats]);
  const handleStarClick = async (rating) => {
    if (submitting) return;
    const hadPreviousRating = userRating !== null;
    const previousRating = userRating;
    setSubmitting(true);
    setErrorMessage(null);
    setHoveredStar(null);
    setUserRating(rating);
    setStatusMessage("Saving\u2026");
    try {
      const result = await ratingAPI.submitRating(itemId, itemType, rating);
      if (result && result.success) {
        if (hadPreviousRating) {
          if (previousRating === rating) {
            setStatusMessage("Rating confirmed!");
          } else {
            setStatusMessage(`Updated to ${rating} star${rating !== 1 ? "s" : ""}!`);
          }
        } else {
          setStatusMessage("Thanks for rating!");
        }
        setStats(result.stats);
        onRate?.(rating, result.stats);
      } else {
        setUserRating(previousRating);
        setErrorMessage("Could not save. Please try again.");
        setStatusMessage(null);
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
      setUserRating(previousRating);
      setErrorMessage("Something went wrong.");
      setStatusMessage(null);
    } finally {
      setSubmitting(false);
    }
  };
  const renderStar = (index) => {
    const isHovered = hoveredStar !== null && index <= hoveredStar;
    const isUserRated = userRating !== null && index <= userRating;
    const fillColor = isHovered || isUserRated ? starColorClasses[starColor].hex : "#94a3b8";
    const ringClass = starColorClasses[starColor].ring;
    return /* @__PURE__ */ jsx6(
      "button",
      {
        onClick: () => void handleStarClick(index),
        onMouseEnter: () => setHoveredStar(index),
        onMouseLeave: () => setHoveredStar(null),
        disabled: submitting,
        type: "button",
        className: `${sizeClasses[size]} transition-all duration-150 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 ${ringClass}`,
        "aria-label": `Rate ${index} stars`,
        children: /* @__PURE__ */ jsx6(
          "svg",
          {
            xmlns: "http://www.w3.org/2000/svg",
            className: "w-full h-full transition-all duration-150",
            viewBox: "0 0 20 20",
            children: /* @__PURE__ */ jsx6(
              "path",
              {
                d: "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z",
                fill: fillColor
              }
            )
          }
        )
      },
      index
    );
  };
  const renderAggregateStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const accentColor = starColorClasses[starColor].hex;
    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(
          /* @__PURE__ */ jsx6(
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              className: sizeClasses[size],
              viewBox: "0 0 20 20",
              children: /* @__PURE__ */ jsx6(
                "path",
                {
                  d: "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z",
                  fill: accentColor
                }
              )
            },
            i
          )
        );
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(
          /* @__PURE__ */ jsxs6("div", { className: "relative", children: [
            /* @__PURE__ */ jsx6(
              "svg",
              {
                xmlns: "http://www.w3.org/2000/svg",
                className: sizeClasses[size],
                viewBox: "0 0 20 20",
                children: /* @__PURE__ */ jsx6(
                  "path",
                  {
                    d: "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z",
                    fill: "#d1d5db"
                  }
                )
              }
            ),
            /* @__PURE__ */ jsx6("div", { className: "absolute inset-0 overflow-hidden", style: { width: "50%" }, children: /* @__PURE__ */ jsx6(
              "svg",
              {
                xmlns: "http://www.w3.org/2000/svg",
                className: sizeClasses[size],
                viewBox: "0 0 20 20",
                children: /* @__PURE__ */ jsx6(
                  "path",
                  {
                    d: "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z",
                    fill: accentColor
                  }
                )
              }
            ) })
          ] }, i)
        );
      } else {
        stars.push(
          /* @__PURE__ */ jsx6(
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              className: sizeClasses[size],
              viewBox: "0 0 20 20",
              children: /* @__PURE__ */ jsx6(
                "path",
                {
                  d: "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z",
                  fill: "#d1d5db"
                }
              )
            },
            i
          )
        );
      }
    }
    return stars;
  };
  if (loading) {
    return /* @__PURE__ */ jsxs6("div", { className: "flex items-center gap-2 animate-pulse", children: [
      /* @__PURE__ */ jsx6("div", { className: "flex gap-1", children: Array.from({ length: 5 }).map((_, i) => /* @__PURE__ */ jsx6("div", { className: `${sizeClasses[size]} bg-gray-300 rounded` }, `loading-star-${i}`)) }),
      /* @__PURE__ */ jsx6("div", { className: `${textSizeClasses[size]} text-text-muted`, children: "Loading ratings..." })
    ] });
  }
  if (fetchError) {
    return /* @__PURE__ */ jsx6("div", { className: `${textSizeClasses[size]} text-text-muted`, children: fetchError });
  }
  const hasRatings = stats && stats.total_ratings > 0;
  return /* @__PURE__ */ jsxs6("div", { className: "space-y-4", children: [
    hasRatings && /* @__PURE__ */ jsxs6("div", { className: "flex items-center gap-3", children: [
      /* @__PURE__ */ jsx6("div", { className: "flex gap-1", children: renderAggregateStars(stats.average_rating) }),
      /* @__PURE__ */ jsx6("div", { className: `${textSizeClasses[size]} font-semibold text-text-primary`, children: stats.average_rating.toFixed(1) }),
      /* @__PURE__ */ jsxs6("div", { className: `${textSizeClasses[size]} text-text-muted`, children: [
        "(",
        stats.total_ratings,
        " ",
        stats.total_ratings === 1 ? "rating" : "ratings",
        ")"
      ] })
    ] }),
    /* @__PURE__ */ jsxs6("div", { children: [
      /* @__PURE__ */ jsx6("h4", { className: `${textSizeClasses[size]} font-semibold mb-2 text-text-primary`, children: userRating ? "Your rating" : "Rate this " + itemType }),
      /* @__PURE__ */ jsx6("div", { className: "flex gap-1", "aria-live": "polite", children: [1, 2, 3, 4, 5].map((i) => renderStar(i)) }),
      userRating && /* @__PURE__ */ jsxs6("p", { className: `${textSizeClasses[size]} text-text-muted mt-2`, children: [
        "You rated this ",
        userRating,
        " star",
        userRating !== 1 ? "s" : ""
      ] }),
      statusMessage && /* @__PURE__ */ jsx6("p", { className: `${textSizeClasses[size]} text-text-muted mt-2`, role: "status", children: statusMessage }),
      errorMessage && /* @__PURE__ */ jsx6("p", { className: `${textSizeClasses[size]} text-red-400 mt-2`, role: "alert", children: errorMessage })
    ] }),
    !hasRatings && !userRating && /* @__PURE__ */ jsxs6("p", { className: `${textSizeClasses[size]} text-text-muted italic`, children: [
      "Be the first to rate this ",
      itemType,
      "!"
    ] })
  ] });
};

// src/components/display/ContentCard.tsx
import { Tag as TagIcon } from "lucide-react";
import { memo } from "react";
import { Link } from "react-router-dom";
import { jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
var severityColors = {
  Critical: "bg-red-500/20 text-red-400 border-red-500/50",
  High: "bg-orange-500/20 text-orange-400 border-orange-500/50",
  Medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50",
  Low: "bg-green-500/20 text-green-400 border-green-500/50"
};
var accentColors5 = {
  violet: "hover:border-violet-500/50 hover:shadow-violet-500/10",
  blue: "hover:border-blue-500/50 hover:shadow-blue-500/10",
  green: "hover:border-green-500/50 hover:shadow-green-500/10",
  red: "hover:border-red-500/50 hover:shadow-red-500/10",
  yellow: "hover:border-yellow-500/50 hover:shadow-yellow-500/10",
  teal: "hover:border-teal-500/50 hover:shadow-teal-500/10"
};
var accentFocusRings = {
  violet: "focus-visible:ring-violet-400/60",
  blue: "focus-visible:ring-blue-400/60",
  green: "focus-visible:ring-green-400/60",
  red: "focus-visible:ring-red-400/60",
  yellow: "focus-visible:ring-yellow-400/60",
  teal: "focus-visible:ring-teal-400/60"
};
var accentTextColors = {
  violet: "group-hover:text-violet-400",
  blue: "group-hover:text-blue-400",
  green: "group-hover:text-green-400",
  red: "group-hover:text-red-400",
  yellow: "group-hover:text-yellow-400",
  teal: "group-hover:text-teal-400"
};
var tagHoverColors = {
  violet: "text-violet-200 hover:text-white hover:bg-violet-500/20 focus-visible:ring-violet-400/50",
  blue: "text-blue-200 hover:text-white hover:bg-blue-500/20 focus-visible:ring-blue-400/50",
  green: "text-green-200 hover:text-white hover:bg-green-500/20 focus-visible:ring-green-400/50",
  red: "text-red-200 hover:text-white hover:bg-red-500/20 focus-visible:ring-red-400/50",
  yellow: "text-yellow-200 hover:text-yellow-50 hover:bg-yellow-500/20 focus-visible:ring-yellow-400/50",
  teal: "text-teal-200 hover:text-white hover:bg-teal-500/20 focus-visible:ring-teal-400/50"
};
var progressBarColors = {
  violet: "bg-violet-500",
  blue: "bg-blue-500",
  green: "bg-green-500",
  red: "bg-red-500",
  yellow: "bg-yellow-500",
  teal: "bg-teal-500"
};
var ContentCard = memo(function ContentCard2({
  href,
  title,
  excerpt,
  date,
  readTime,
  durationMinutes,
  tags = [],
  onTagClick,
  featured,
  metadata,
  metadataIcon,
  severity,
  status,
  accentColor = "violet",
  animationDelay = 0,
  className = "",
  image,
  imageAlt,
  variant = "default",
  isLoading = false,
  progress
}) {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };
  if (isLoading) {
    return /* @__PURE__ */ jsxs7(
      "div",
      {
        className: `flex flex-col h-full bg-surface-raised rounded-2xl p-6 border border-surface-border ${className}`,
        "aria-busy": "true",
        "aria-label": "Loading content card",
        children: [
          image && /* @__PURE__ */ jsx7("div", { className: "w-full h-48 bg-surface-hover rounded-xl mb-4 animate-pulse" }),
          /* @__PURE__ */ jsx7("div", { className: "h-8 bg-surface-hover rounded mb-3 animate-pulse w-3/4" }),
          /* @__PURE__ */ jsx7("div", { className: "h-4 bg-surface-hover rounded mb-2 animate-pulse" }),
          /* @__PURE__ */ jsx7("div", { className: "h-4 bg-surface-hover rounded mb-2 animate-pulse w-5/6" }),
          /* @__PURE__ */ jsx7("div", { className: "h-4 bg-surface-hover rounded mb-4 animate-pulse w-2/3" }),
          /* @__PURE__ */ jsxs7("div", { className: "flex gap-2", children: [
            /* @__PURE__ */ jsx7("div", { className: "h-6 bg-surface-hover rounded-full animate-pulse w-16" }),
            /* @__PURE__ */ jsx7("div", { className: "h-6 bg-surface-hover rounded-full animate-pulse w-16" })
          ] })
        ]
      }
    );
  }
  const variantStyles = {
    default: "p-6",
    compact: "p-4 flex-row gap-4",
    expanded: "p-8"
  };
  const tagAreaSpacing = variant === "compact" ? "px-4 pb-5 pt-4" : variant === "expanded" ? "px-8 pb-10 pt-5" : "px-6 pb-8 pt-5";
  return /* @__PURE__ */ jsxs7(
    Link,
    {
      to: href,
      className: `relative flex flex-col h-full bg-surface-raised rounded-2xl border border-surface-border shadow-lg hover:shadow-2xl hover:-translate-y-1 hover:scale-[1.02] transition-all duration-300 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-raised ${accentColors5[accentColor]} ${accentFocusRings[accentColor]} ${className}`,
      style: { animationDelay: `${animationDelay}ms` },
      children: [
        progress !== void 0 && progress > 0 && /* @__PURE__ */ jsx7("div", { className: "absolute top-0 left-0 right-0 h-1 bg-surface-hover rounded-t-2xl overflow-hidden", children: /* @__PURE__ */ jsx7(
          "div",
          {
            className: `h-full ${progressBarColors[accentColor]} transition-all duration-300`,
            style: { width: `${progress}%` },
            role: "progressbar",
            "aria-valuenow": progress,
            "aria-valuemin": 0,
            "aria-valuemax": 100,
            "aria-label": "Reading progress"
          }
        ) }),
        /* @__PURE__ */ jsxs7(
          "div",
          {
            className: `flex flex-col flex-grow ${variantStyles[variant]} ${variant === "compact" ? "flex-row gap-4" : "flex-col"}`,
            children: [
              image && /* @__PURE__ */ jsx7("div", { className: variant === "compact" ? "w-1/3 flex-shrink-0" : "w-full", children: /* @__PURE__ */ jsx7(
                "img",
                {
                  src: image,
                  alt: imageAlt || title,
                  loading: "lazy",
                  decoding: "async",
                  className: `object-cover rounded-xl ${variant === "compact" ? "h-full" : "h-48 w-full mb-4"}`
                }
              ) }),
              /* @__PURE__ */ jsxs7("div", { className: "flex flex-col flex-grow", children: [
                featured && /* @__PURE__ */ jsx7("span", { className: "inline-block px-3 py-1 bg-violet-500/20 text-violet-400 text-xs rounded-full mb-3 self-start", children: "Featured" }),
                /* @__PURE__ */ jsx7(
                  "h2",
                  {
                    className: `${variant === "compact" ? "text-lg" : variant === "expanded" ? "text-2xl" : "text-xl"} font-semibold mb-3 transition-colors ${accentTextColors[accentColor]}`,
                    children: title
                  }
                ),
                /* @__PURE__ */ jsx7(
                  "p",
                  {
                    className: `text-text-muted text-base leading-relaxed mb-4 ${variant === "compact" ? "line-clamp-2" : "line-clamp-3"}`,
                    children: excerpt
                  }
                ),
                (date || readTime || durationMinutes) && /* @__PURE__ */ jsxs7("div", { className: "flex flex-wrap items-center gap-4 text-xs text-text-muted mb-3", children: [
                  date && /* @__PURE__ */ jsx7("span", { children: formatDate(date) }),
                  readTime && /* @__PURE__ */ jsxs7("span", { children: [
                    readTime,
                    " min read"
                  ] }),
                  durationMinutes && /* @__PURE__ */ jsxs7("span", { children: [
                    durationMinutes,
                    " min"
                  ] })
                ] }),
                (metadata || severity || status) && /* @__PURE__ */ jsxs7("div", { className: "flex items-center gap-2 flex-wrap mb-3", children: [
                  metadata && /* @__PURE__ */ jsxs7("div", { className: "flex items-center gap-1 text-xs text-text-muted", children: [
                    metadataIcon,
                    /* @__PURE__ */ jsx7("span", { children: metadata })
                  ] }),
                  severity && /* @__PURE__ */ jsx7(
                    "span",
                    {
                      className: `inline-flex items-center px-2.5 py-1 min-h-[28px] text-xs rounded-full border ${severityColors[severity]}`,
                      children: severity
                    }
                  ),
                  status && /* @__PURE__ */ jsx7("span", { className: "inline-flex items-center px-2.5 py-1 min-h-[28px] bg-surface-hover text-text-primary text-xs rounded-full", children: status })
                ] })
              ] })
            ]
          }
        ),
        tags.length > 0 && /* @__PURE__ */ jsx7("div", { className: `flex flex-wrap gap-2 ${tagAreaSpacing}`, "aria-label": "Content tags", children: tags.map((tag) => /* @__PURE__ */ jsxs7(
          "span",
          {
            role: onTagClick ? "button" : void 0,
            tabIndex: onTagClick ? 0 : void 0,
            onClick: (e) => {
              if (onTagClick) {
                e.preventDefault();
                e.stopPropagation();
                onTagClick(tag);
              }
            },
            onKeyDown: (e) => {
              if (onTagClick && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                e.stopPropagation();
                onTagClick(tag);
              }
            },
            className: `inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] bg-surface-hover text-text-muted text-xs rounded-full ${tagHoverColors[accentColor]} transition-colors ${onTagClick ? "cursor-pointer" : "cursor-default"}`,
            children: [
              /* @__PURE__ */ jsx7(TagIcon, { size: 12 }),
              tag
            ]
          },
          tag
        )) })
      ]
    }
  );
});

// src/components/display/StarRating.tsx
import { Star } from "lucide-react";
import { useEffect as useEffect4, useState as useState4 } from "react";
import { jsx as jsx8, jsxs as jsxs8 } from "react/jsx-runtime";
var filledStarClassMap = {
  "yellow-400": "fill-yellow-400 text-yellow-400",
  "violet-400": "fill-violet-400 text-violet-400",
  "blue-400": "fill-blue-400 text-blue-400"
};
var getStorage = () => {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return null;
  }
  return window.localStorage;
};
function StarRating({
  itemId,
  storagePrefix = "content",
  onRate,
  starColor = "yellow-400",
  starSize = 24
}) {
  const [userRating, setUserRating] = useState4(0);
  const [averageRating, setAverageRating] = useState4(0);
  const [totalRatings, setTotalRatings] = useState4(0);
  const [hoveredStar, setHoveredStar] = useState4(0);
  useEffect4(() => {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    const ratingsKey = `${storagePrefix}-ratings-${itemId}`;
    const userRatingKey = `${storagePrefix}-user-rating-${itemId}`;
    try {
      const storedRatings = storage.getItem(ratingsKey);
      const storedUserRating = storage.getItem(userRatingKey);
      if (storedRatings) {
        const parsed = JSON.parse(storedRatings);
        if (typeof parsed === "object" && parsed !== null && "total" in parsed && "count" in parsed && typeof parsed.total === "number" && typeof parsed.count === "number") {
          const { total, count } = parsed;
          setAverageRating(count > 0 ? total / count : 0);
          setTotalRatings(count);
        }
      }
      if (storedUserRating) {
        const rating = parseInt(storedUserRating, 10);
        if (!isNaN(rating) && rating >= 1 && rating <= 5) {
          setUserRating(rating);
        }
      }
    } catch {
    }
  }, [itemId, storagePrefix]);
  const persistRating = (rating) => {
    const storage = getStorage();
    if (!storage) {
      return { total: rating, count: 1 };
    }
    const ratingsKey = `${storagePrefix}-ratings-${itemId}`;
    const userRatingKey = `${storagePrefix}-user-rating-${itemId}`;
    let total = 0;
    let count = 0;
    try {
      const storedRatings = storage.getItem(ratingsKey);
      if (storedRatings) {
        const parsed = JSON.parse(storedRatings);
        if (typeof parsed === "object" && parsed !== null && "total" in parsed && "count" in parsed && typeof parsed.total === "number" && typeof parsed.count === "number") {
          total = parsed.total;
          count = parsed.count;
        }
      }
      if (userRating > 0 && count > 0) {
        total -= userRating;
        count -= 1;
      }
      total += rating;
      count += 1;
      storage.setItem(ratingsKey, JSON.stringify({ total, count }));
      storage.setItem(userRatingKey, rating.toString());
    } catch {
    }
    return { total, count };
  };
  const handleRate = (rating) => {
    const { total, count } = persistRating(rating);
    setUserRating(rating);
    setAverageRating(count > 0 ? total / count : rating);
    setTotalRatings(count);
    onRate?.(rating);
  };
  const filledClasses = filledStarClassMap[starColor] ?? filledStarClassMap["yellow-400"];
  return /* @__PURE__ */ jsxs8("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsxs8("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx8("div", { className: "flex gap-1", children: [1, 2, 3, 4, 5].map((star) => {
        const isFilled = star <= (hoveredStar || userRating);
        return /* @__PURE__ */ jsx8(
          "button",
          {
            onClick: () => handleRate(star),
            onMouseEnter: () => setHoveredStar(star),
            onMouseLeave: () => setHoveredStar(0),
            className: "transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded",
            "aria-label": `Rate ${star} stars`,
            children: /* @__PURE__ */ jsx8(
              Star,
              {
                size: starSize,
                className: `transition-colors ${isFilled ? filledClasses : "text-gray-600 hover:text-gray-400"}`
              }
            )
          },
          star
        );
      }) }),
      userRating > 0 && /* @__PURE__ */ jsxs8("span", { className: "text-sm text-gray-400", children: [
        "You rated: ",
        userRating,
        "/5"
      ] })
    ] }),
    totalRatings > 0 && /* @__PURE__ */ jsxs8("div", { className: "text-sm text-gray-500", children: [
      "Average: ",
      averageRating.toFixed(1),
      "/5 (",
      totalRatings,
      " ",
      totalRatings === 1 ? "rating" : "ratings",
      ")"
    ] }),
    userRating === 0 && /* @__PURE__ */ jsx8("p", { className: "text-xs text-gray-500", children: "Click to rate" })
  ] });
}

// src/components/display/StatCard.tsx
import { jsx as jsx9, jsxs as jsxs9 } from "react/jsx-runtime";
var sizeClasses2 = {
  sm: {
    padding: "p-4",
    icon: "mb-2",
    value: "text-xl font-bold",
    label: "text-xs"
  },
  md: {
    padding: "p-6",
    icon: "mb-3",
    value: "text-2xl font-bold",
    label: "text-sm"
  },
  lg: {
    padding: "p-8",
    icon: "mb-3",
    value: "text-3xl font-bold",
    label: "text-sm"
  }
};
var colorClasses = {
  blue: "text-blue-500 dark:text-blue-400",
  teal: "text-teal-500 dark:text-teal-400",
  violet: "text-violet-500 dark:text-violet-400",
  emerald: "text-emerald-500 dark:text-emerald-400",
  amber: "text-amber-500 dark:text-amber-400"
};
function StatCard({
  icon,
  value,
  label,
  colorScheme = "blue",
  size = "md",
  className = ""
}) {
  const sizes = sizeClasses2[size];
  const iconColor = colorClasses[colorScheme];
  return /* @__PURE__ */ jsxs9(
    "div",
    {
      className: `bg-surface-raised border border-surface-border ${sizes.padding} rounded-lg text-center shadow-lg dark:shadow-black/30 hover:shadow-2xl hover:-translate-y-1 hover:scale-[1.02] transition-all ${className}`,
      children: [
        /* @__PURE__ */ jsx9("div", { className: `flex justify-center ${sizes.icon} ${iconColor}`, children: icon }),
        /* @__PURE__ */ jsx9("p", { className: `${sizes.value} text-text-primary mb-1`, children: value }),
        /* @__PURE__ */ jsx9("p", { className: `${sizes.label} text-text-muted`, children: label })
      ]
    }
  );
}

// src/components/feedback/EmptyState.tsx
import { SearchX } from "lucide-react";
import { jsx as jsx10, jsxs as jsxs10 } from "react/jsx-runtime";
var accentColors6 = {
  violet: {
    icon: "text-brand-accent",
    title: "text-text-accent",
    button: "bg-interactive-active hover:bg-interactive-hover"
  },
  emerald: {
    icon: "text-brand-accent",
    title: "text-text-accent",
    button: "bg-interactive-active hover:bg-interactive-hover"
  },
  blue: {
    icon: "text-brand-accent",
    title: "text-text-accent",
    button: "bg-interactive-active hover:bg-interactive-hover"
  },
  amber: {
    icon: "text-brand-accent",
    title: "text-text-accent",
    button: "bg-interactive-active hover:bg-interactive-hover"
  },
  rose: {
    icon: "text-brand-accent",
    title: "text-text-accent",
    button: "bg-interactive-active hover:bg-interactive-hover"
  },
  gray: {
    icon: "text-text-muted",
    title: "text-text-primary",
    button: "bg-interactive-default hover:bg-interactive-hover"
  }
};
function EmptyState({
  icon,
  title,
  description,
  action,
  accentColor = "gray",
  minHeight = "300px",
  className = ""
}) {
  const colors = accentColors6[accentColor];
  const defaultIcon = /* @__PURE__ */ jsx10(SearchX, { size: 48, className: "mx-auto" });
  return /* @__PURE__ */ jsxs10(
    "div",
    {
      className: `
        text-center py-12 px-4
        flex flex-col justify-center items-center
        ${className}
      `,
      style: { minHeight },
      children: [
        /* @__PURE__ */ jsx10("div", { className: `mb-4 ${colors.icon}`, children: icon || defaultIcon }),
        /* @__PURE__ */ jsx10("h3", { className: `text-xl font-semibold mb-2 ${colors.title}`, children: title }),
        description && /* @__PURE__ */ jsx10("p", { className: "text-text-muted text-sm max-w-md mb-4", children: description }),
        action && /* @__PURE__ */ jsx10(
          "button",
          {
            onClick: action.onClick,
            className: `
            mt-2 px-4 py-2
            ${colors.button}
            text-white rounded-lg
            transition-colors
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-interactive-focus
          `,
            children: action.label
          }
        )
      ]
    }
  );
}

// src/components/feedback/Error.tsx
import DOMPurify from "dompurify";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Component } from "react";

// src/utils/env.ts
var isDevelopmentEnvironment = (() => {
  if (typeof process !== "undefined" && process?.env?.NODE_ENV === "production") {
    return false;
  }
  try {
    const mode = import.meta.env?.MODE;
    return mode !== "production";
  } catch {
    return typeof process !== "undefined" && process?.env?.NODE_ENV !== "production";
  }
})();
function getSentry() {
  if (typeof window !== "undefined" && window.Sentry) {
    return window.Sentry;
  }
  return null;
}

// src/components/feedback/Error.tsx
import { jsx as jsx11, jsxs as jsxs11 } from "react/jsx-runtime";
var errorBorderColors = {
  violet: "border-violet-500",
  blue: "border-blue-500",
  green: "border-green-500",
  red: "border-red-500",
  yellow: "border-yellow-500",
  gray: "border-gray-500"
};
var errorBgColors = {
  violet: "bg-violet-900/20",
  blue: "bg-blue-900/20",
  green: "bg-green-900/20",
  red: "bg-red-900/20",
  yellow: "bg-yellow-900/20",
  gray: "bg-surface-raised/80"
};
var errorTextColors = {
  violet: "text-violet-400",
  blue: "text-blue-400",
  green: "text-green-400",
  red: "text-red-400",
  yellow: "text-yellow-400",
  gray: "text-text-muted"
};
var errorButtonColors = {
  violet: "bg-violet-600 hover:bg-violet-700",
  blue: "bg-blue-600 hover:bg-blue-700",
  green: "bg-green-600 hover:bg-green-700",
  red: "bg-red-600 hover:bg-red-700",
  yellow: "bg-yellow-600 hover:bg-yellow-700",
  gray: "bg-interactive-default hover:bg-interactive-hover"
};
function ErrorMessage({
  error,
  onRetry,
  className = "",
  variant = "red"
}) {
  const errorMessage = typeof error === "string" ? error : error && typeof error === "object" && "message" in error ? error.message : "An unexpected error occurred. Please try again.";
  const sanitizedErrorMessage = { __html: DOMPurify.sanitize(errorMessage || "") };
  return /* @__PURE__ */ jsx11(
    "div",
    {
      className: `${errorBgColors[variant]} border ${errorBorderColors[variant]} rounded-lg p-4 ${className}`,
      children: /* @__PURE__ */ jsxs11("div", { className: "flex items-start gap-3", children: [
        /* @__PURE__ */ jsx11(AlertCircle, { className: `${errorTextColors[variant]} flex-shrink-0 mt-0.5`, size: 20 }),
        /* @__PURE__ */ jsxs11("div", { className: "flex-1", children: [
          /* @__PURE__ */ jsx11("h3", { className: `${errorTextColors[variant]} font-semibold mb-1`, children: "Error" }),
          /* @__PURE__ */ jsx11("p", { className: `${errorTextColors[variant]} text-sm`, children: sanitizedErrorMessage.__html && sanitizedErrorMessage.__html.length > 0 ? /* @__PURE__ */ jsx11("span", { dangerouslySetInnerHTML: sanitizedErrorMessage }) : errorMessage }),
          onRetry && /* @__PURE__ */ jsxs11(
            "button",
            {
              onClick: onRetry,
              className: `mt-3 flex items-center gap-2 px-3 py-1.5 ${errorButtonColors[variant]} text-white text-sm rounded transition-colors`,
              children: [
                /* @__PURE__ */ jsx11(RefreshCw, { size: 14 }),
                "Try Again"
              ]
            }
          )
        ] })
      ] })
    }
  );
}
function ErrorCard({ error, onRetry, variant = "red" }) {
  const errorMessage = typeof error === "string" ? error : error && typeof error === "object" && "message" in error ? error.message : "We encountered an error while loading this content.";
  const sanitizedErrorMessage = { __html: DOMPurify.sanitize(errorMessage || "") };
  return /* @__PURE__ */ jsxs11("div", { className: "bg-surface-raised rounded-lg p-8 text-center border border-surface-border", children: [
    /* @__PURE__ */ jsx11(
      "div",
      {
        className: `mx-auto w-16 h-16 ${errorBgColors[variant]} rounded-full flex items-center justify-center mb-4`,
        children: /* @__PURE__ */ jsx11(AlertCircle, { className: errorTextColors[variant], size: 32 })
      }
    ),
    /* @__PURE__ */ jsx11("h3", { className: `text-xl font-semibold ${errorTextColors[variant]} mb-2`, children: "Something went wrong" }),
    /* @__PURE__ */ jsx11("p", { className: "text-text-muted mb-4", children: sanitizedErrorMessage.__html && sanitizedErrorMessage.__html.length > 0 ? /* @__PURE__ */ jsx11("span", { dangerouslySetInnerHTML: sanitizedErrorMessage }) : errorMessage }),
    onRetry && /* @__PURE__ */ jsxs11(
      "button",
      {
        onClick: onRetry,
        className: `flex items-center gap-2 mx-auto px-4 py-2 ${errorButtonColors[variant]} text-white rounded transition-colors`,
        children: [
          /* @__PURE__ */ jsx11(RefreshCw, { size: 16 }),
          "Try Again"
        ]
      }
    )
  ] });
}
function ErrorPage({ error, onRetry, variant = "red" }) {
  return /* @__PURE__ */ jsx11("div", { className: "flex flex-col justify-center items-center min-h-[calc(100vh-200px)] text-white px-4", children: /* @__PURE__ */ jsx11("div", { className: "max-w-md w-full", children: /* @__PURE__ */ jsx11(ErrorCard, { error, onRetry, variant }) }) });
}
var ErrorBoundary = class extends Component {
  constructor() {
    super(...arguments);
    __publicField(this, "state", {
      hasError: false
    });
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    const { onError, showDevDetails = true, enableSentry = true, errorContext = {} } = this.props;
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      url: typeof window !== "undefined" ? window.location.href : "unknown",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      context: errorContext
    };
    if (isDevelopmentEnvironment && showDevDetails) {
      console.error("[ErrorBoundary] Caught error:", errorData);
    }
    if (enableSentry) {
      const Sentry = getSentry();
      if (Sentry) {
        Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack
            },
            custom: errorContext
          },
          tags: {
            errorBoundary: "true",
            location: typeof window !== "undefined" ? window.location.pathname : "unknown"
          },
          level: "error"
        });
      }
    }
    if (onError) {
      onError(error, errorInfo);
    }
    this.setState({ errorInfo });
  }
  render() {
    const {
      children,
      fallback,
      homePath = "/",
      variant = "violet",
      showDevDetails = true
    } = this.props;
    if (this.state.hasError) {
      if (fallback) {
        return fallback;
      }
      const buttonClasses = errorButtonColors[variant];
      return /* @__PURE__ */ jsx11("div", { className: "min-h-screen flex items-center justify-center bg-surface-base px-4", children: /* @__PURE__ */ jsxs11("div", { className: "max-w-md w-full text-center", children: [
        /* @__PURE__ */ jsx11("div", { className: "text-6xl mb-6", children: "\u26A0\uFE0F" }),
        /* @__PURE__ */ jsx11("h1", { className: "text-3xl font-bold mb-4 text-text-primary", children: "Something went wrong" }),
        /* @__PURE__ */ jsx11("p", { className: "text-text-muted mb-6", children: "We're sorry, but something unexpected happened. The error has been logged." }),
        isDevelopmentEnvironment && showDevDetails && this.state.error && /* @__PURE__ */ jsxs11("details", { className: "mb-6 text-left bg-surface-raised p-4 rounded-lg border border-surface-border", children: [
          /* @__PURE__ */ jsx11(
            "summary",
            {
              className: `cursor-pointer ${errorTextColors[variant]} font-semibold mb-2`,
              children: "Error Details (Dev Only)"
            }
          ),
          /* @__PURE__ */ jsxs11("pre", { className: "text-xs text-red-400 overflow-auto", children: [
            this.state.error.toString(),
            this.state.errorInfo && this.state.errorInfo.componentStack
          ] })
        ] }),
        /* @__PURE__ */ jsxs11("div", { className: "flex flex-col sm:flex-row gap-4 justify-center", children: [
          /* @__PURE__ */ jsx11(
            "button",
            {
              onClick: () => window.location.reload(),
              className: `px-6 py-3 ${buttonClasses} text-white font-semibold rounded-lg transition`,
              children: "Reload Page"
            }
          ),
          /* @__PURE__ */ jsx11(
            "a",
            {
              href: homePath,
              className: "px-6 py-3 bg-surface-raised hover:bg-surface-hover text-text-primary border border-surface-border font-semibold rounded-lg transition",
              children: "Go to Home"
            }
          )
        ] })
      ] }) });
    }
    return children;
  }
};

// src/components/feedback/Loading.tsx
import { Loader2 } from "lucide-react";
import { useEffect as useEffect5, useRef as useRef3 } from "react";
import { jsx as jsx12, jsxs as jsxs12 } from "react/jsx-runtime";
var spinnerColors = {
  violet: "text-violet-400",
  blue: "text-blue-400",
  green: "text-green-400",
  red: "text-red-400",
  yellow: "text-yellow-400",
  gray: "text-gray-400"
};
function LoadingSpinner({
  size = 48,
  className = "",
  variant = "blue",
  trackSlowLoadingAfter = 5e3,
  loadingContext = "unknown"
}) {
  const startTimeRef = useRef3(0);
  const trackedRef = useRef3(false);
  useEffect5(() => {
    startTimeRef.current = Date.now();
    trackedRef.current = false;
    const timer = setTimeout(() => {
      if (!trackedRef.current) {
        trackedRef.current = true;
        const duration = Date.now() - startTimeRef.current;
        if (isDevelopmentEnvironment) {
          console.warn("[LoadingSpinner] Slow loading detected:", {
            context: loadingContext,
            duration,
            threshold: trackSlowLoadingAfter
          });
        }
        const Sentry = getSentry();
        if (Sentry) {
          Sentry.captureMessage("Slow loading detected", "warning", {
            tags: {
              component: "LoadingSpinner",
              context: loadingContext
            },
            extra: {
              duration,
              threshold: trackSlowLoadingAfter
            }
          });
        }
      }
    }, trackSlowLoadingAfter);
    return () => {
      clearTimeout(timer);
      const totalDuration = Date.now() - startTimeRef.current;
      if (totalDuration > trackSlowLoadingAfter && isDevelopmentEnvironment) {
        console.log("[LoadingSpinner] Completed after slow load:", {
          context: loadingContext,
          duration: totalDuration
        });
      }
    };
  }, [trackSlowLoadingAfter, loadingContext]);
  return /* @__PURE__ */ jsxs12(
    "div",
    {
      className: `flex items-center justify-center ${className}`,
      role: "status",
      "aria-live": "polite",
      children: [
        /* @__PURE__ */ jsx12(
          Loader2,
          {
            size,
            className: `animate-spin ${spinnerColors[variant]}`,
            "aria-hidden": "true"
          }
        ),
        /* @__PURE__ */ jsx12("span", { className: "sr-only", children: "Loading..." })
      ]
    }
  );
}
function LoadingCard({ message = "Loading...", variant = "blue" }) {
  return /* @__PURE__ */ jsxs12("div", { className: "rounded-lg p-8 text-center border bg-surface-raised border-surface-border text-text-primary", children: [
    /* @__PURE__ */ jsx12(LoadingSpinner, { size: 48, className: "mb-4", variant }),
    /* @__PURE__ */ jsx12("p", { className: "text-text-muted", children: message })
  ] });
}
function LoadingPage({ message = "Loading...", variant = "blue" }) {
  return /* @__PURE__ */ jsxs12("div", { className: "flex flex-col justify-center items-center min-h-[calc(100vh-200px)] text-text-primary", children: [
    /* @__PURE__ */ jsx12(LoadingSpinner, { size: 64, variant }),
    /* @__PURE__ */ jsx12("p", { className: "mt-4 text-lg text-text-muted", children: message })
  ] });
}
function Skeleton({ className = "" }) {
  return /* @__PURE__ */ jsx12(
    "div",
    {
      className: `animate-pulse rounded ${className}`,
      style: { backgroundColor: "var(--theme-surface-hover, #1e293b)" },
      "aria-live": "polite",
      "aria-busy": "true"
    }
  );
}
function SkeletonText({ lines = 3 }) {
  return /* @__PURE__ */ jsx12("div", { className: "space-y-3", children: Array.from({ length: lines }).map((_, i) => /* @__PURE__ */ jsx12(Skeleton, { className: `h-4 ${i === lines - 1 ? "w-5/6" : "w-full"}` }, i)) });
}
function SkeletonCard() {
  return /* @__PURE__ */ jsxs12("div", { className: "rounded-lg border border-surface-border bg-surface-raised shadow-lg p-6", children: [
    /* @__PURE__ */ jsx12(Skeleton, { className: "h-6 w-3/4 mb-4" }),
    /* @__PURE__ */ jsx12(SkeletonText, { lines: 3 }),
    /* @__PURE__ */ jsx12(Skeleton, { className: "h-10 w-32 mt-6" })
  ] });
}

// src/components/forms/ContactForm.tsx
import {
  startTransition,
  useId,
  useOptimistic,
  useState as useState5
} from "react";

// src/components/ui/Button.tsx
import { Link as RouterLink } from "react-router-dom";
import { Fragment as Fragment2, jsx as jsx13, jsxs as jsxs13 } from "react/jsx-runtime";
var baseClasses = "inline-flex items-center justify-center gap-1.5 rounded-md text-sm font-medium shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
var sizeClasses3 = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2",
  lg: "px-6 py-3 text-base"
};
var variantClasses = {
  primary: "bg-[var(--color-interactive-default)] hover:bg-[var(--color-interactive-hover)] text-[var(--color-text-inverse)] focus:ring-[var(--color-interactive-focus)]",
  secondary: "bg-[var(--color-surface-raised)] hover:bg-[var(--color-surface-hover)] text-[var(--color-text-primary)] border border-[var(--color-surface-border)] focus:ring-[var(--color-interactive-focus)]",
  danger: "bg-[var(--color-status-error)] hover:bg-[var(--color-status-error)]/90 text-white focus:ring-[var(--color-status-error)]",
  warning: "bg-[var(--color-status-warning)] hover:bg-[var(--color-status-warning)]/90 text-[var(--color-text-primary)] focus:ring-[var(--color-status-warning)]",
  success: "bg-[var(--color-status-success)] hover:bg-[var(--color-status-success)]/90 text-white focus:ring-[var(--color-status-success)]",
  outline: "border border-[var(--color-surface-border)] bg-transparent hover:bg-[var(--color-surface-hover)] text-[var(--color-text-primary)] focus:ring-[var(--color-interactive-focus)]",
  ghost: "bg-transparent hover:bg-[var(--color-surface-hover)] text-[var(--color-text-primary)] focus:ring-[var(--color-interactive-focus)]"
};
var Spinner = ({ className = "" }) => /* @__PURE__ */ jsxs13(
  "svg",
  {
    className: `h-4 w-4 animate-spin text-current ${className}`,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx13("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }),
      /* @__PURE__ */ jsx13("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" })
    ]
  }
);
function Button({
  as: ComponentType = "button",
  to,
  variant = "primary",
  size = "md",
  tone: _tone,
  // Deprecated, keeping for backwards compatibility
  isLoading = false,
  disabled = false,
  leftIcon,
  rightIcon,
  children,
  className = "",
  ...props
}) {
  const variantClass = variantClasses[variant];
  const classList = `${baseClasses} ${sizeClasses3[size]} ${variantClass} ${className}`;
  if (ComponentType === "Link") {
    if (!to) {
      throw new Error('Button with as="Link" must have a "to" prop');
    }
    return /* @__PURE__ */ jsxs13(
      RouterLink,
      {
        to,
        className: classList,
        ...props,
        children: [
          isLoading && /* @__PURE__ */ jsx13(Spinner, { className: children ? "mr-2" : "" }),
          !isLoading && leftIcon && /* @__PURE__ */ jsx13("span", { className: children ? "mr-1.5 -ml-0.5" : "", children: leftIcon }),
          children,
          !isLoading && rightIcon && /* @__PURE__ */ jsx13("span", { className: children ? "ml-1.5 -mr-0.5" : "", children: rightIcon })
        ]
      }
    );
  }
  if (ComponentType === "a") {
    const anchorProps = props;
    const href = anchorProps.href || to;
    return /* @__PURE__ */ jsxs13("a", { href, className: classList, ...anchorProps, children: [
      isLoading && /* @__PURE__ */ jsx13(Spinner, { className: children ? "mr-2" : "" }),
      !isLoading && leftIcon && /* @__PURE__ */ jsx13("span", { className: children ? "mr-1.5 -ml-0.5" : "", children: leftIcon }),
      children,
      !isLoading && rightIcon && /* @__PURE__ */ jsx13("span", { className: children ? "ml-1.5 -mr-0.5" : "", children: rightIcon })
    ] });
  }
  return /* @__PURE__ */ jsxs13(
    "button",
    {
      className: classList,
      disabled: disabled || isLoading,
      "aria-busy": isLoading,
      ...props,
      children: [
        isLoading && /* @__PURE__ */ jsxs13(Fragment2, { children: [
          /* @__PURE__ */ jsx13(Spinner, { className: children ? "mr-2" : "" }),
          /* @__PURE__ */ jsx13("span", { className: "sr-only", children: "Loading..." })
        ] }),
        !isLoading && leftIcon && /* @__PURE__ */ jsx13("span", { className: children ? "mr-1.5 -ml-0.5" : "", children: leftIcon }),
        children,
        !isLoading && rightIcon && /* @__PURE__ */ jsx13("span", { className: children ? "ml-1.5 -mr-0.5" : "", children: rightIcon })
      ]
    }
  );
}

// src/components/forms/ContactForm.tsx
import { jsx as jsx14, jsxs as jsxs14 } from "react/jsx-runtime";
var toneStyles = {
  violet: {
    focus: "focus:ring-violet-500 focus:border-violet-400",
    link: "text-violet-300 hover:text-violet-200"
  },
  blue: {
    focus: "focus:ring-blue-500 focus:border-blue-400",
    link: "text-blue-300 hover:text-blue-200"
  },
  emerald: {
    focus: "focus:ring-emerald-500 focus:border-emerald-400",
    link: "text-emerald-300 hover:text-emerald-200"
  },
  sage: {
    focus: "focus:ring-emerald-500 focus:border-emerald-400",
    link: "text-emerald-300 hover:text-emerald-200"
  }
};
var surfaces = {
  dark: {
    card: "bg-gray-900 border border-gray-800 text-gray-100 shadow-2xl shadow-black/40",
    label: "text-gray-300",
    helper: "text-gray-400",
    input: "bg-gray-800 border-gray-700 text-white placeholder-gray-500",
    offline: "border-amber-400/60 bg-amber-900/20 text-amber-100",
    offlineText: "text-amber-100/80",
    success: "bg-gray-800 border border-gray-700 text-gray-100",
    error: "text-rose-300"
  },
  light: {
    card: "bg-white border border-gray-200 text-gray-900 shadow-xl shadow-gray-600/10",
    label: "text-gray-700",
    helper: "text-gray-500",
    input: "bg-white border-gray-300 text-gray-900 placeholder-gray-500",
    offline: "border-amber-200 bg-amber-50 text-amber-800",
    offlineText: "text-amber-700",
    success: "bg-gray-50 border border-gray-200 text-gray-800",
    error: "text-rose-500"
  }
};
function ContactForm({
  endpoint,
  tone = "violet",
  background = "dark",
  title,
  description,
  submitLabel = "Send message",
  successTitle = "Thank you!",
  successMessage = "Your message is in my queue. I'll respond soon.",
  offlineTitle = "Contact form offline",
  offlineMessage = "The form endpoint is not configured. Reach out via email or LinkedIn while we fix this.",
  nameLabel = "Name",
  emailLabel = "Email",
  messageLabel = "Message",
  namePlaceholder = "Your name",
  emailPlaceholder = "you@example.com",
  messagePlaceholder = "How can I help?",
  privacyNotice = "By submitting, you agree to our privacy policy.",
  footer,
  className = "",
  cardClassName = "",
  onSubmitSuccess,
  onSubmitError
}) {
  const [sent, setSent] = useState5(false);
  const [submitting, setSubmitting] = useState5(false);
  const [error, setError] = useState5(null);
  const [honeypot, setHoneypot] = useState5("");
  const honeypotId = useId();
  const [optimisticSent, setOptimisticSent] = useOptimistic(
    sent,
    (_currentState, optimisticValue) => optimisticValue
  );
  const palette = toneStyles[tone];
  const surface = surfaces[background];
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!endpoint) {
      setError("Contact endpoint is not configured.");
      return;
    }
    if (honeypot.trim().length > 0) {
      setSent(true);
      setError(null);
      event.currentTarget.reset();
      return;
    }
    startTransition(() => {
      setSubmitting(true);
      setError(null);
    });
    startTransition(() => {
      setOptimisticSent(true);
    });
    const formData = new FormData(event.currentTarget);
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData
      });
      if (!response.ok) {
        throw new Error("Failed to send message");
      }
      event.currentTarget.reset();
      startTransition(() => {
        setSent(true);
      });
      onSubmitSuccess?.();
    } catch (err) {
      const errorObject = err instanceof Error ? err : new Error("Failed to submit contact form");
      startTransition(() => {
        setError("We couldn't send your message. Please try again or email me directly.");
      });
      onSubmitError?.(errorObject);
    } finally {
      startTransition(() => {
        setSubmitting(false);
      });
    }
  };
  return /* @__PURE__ */ jsxs14("section", { className: `space-y-6 ${className}`, children: [
    (title || description) && /* @__PURE__ */ jsxs14("header", { className: "space-y-2 text-center", children: [
      title && /* @__PURE__ */ jsx14("h2", { className: "text-3xl font-semibold tracking-tight", children: title }),
      description && /* @__PURE__ */ jsx14("p", { className: `text-base ${surface.helper}`, children: description })
    ] }),
    !endpoint && /* @__PURE__ */ jsxs14("div", { className: `rounded-lg px-4 py-3 text-sm ${surface.offline}`, children: [
      /* @__PURE__ */ jsx14("p", { className: "font-semibold", children: offlineTitle }),
      /* @__PURE__ */ jsx14("p", { className: `mt-1 ${surface.offlineText}`, children: offlineMessage })
    ] }),
    optimisticSent ? /* @__PURE__ */ jsxs14("div", { className: `rounded-2xl px-6 py-10 text-center ${surface.success}`, "aria-live": "polite", children: [
      /* @__PURE__ */ jsx14("h3", { className: "text-2xl font-semibold mb-2", children: successTitle }),
      /* @__PURE__ */ jsx14("p", { className: "text-base opacity-90", children: successMessage })
    ] }) : /* @__PURE__ */ jsxs14(
      "form",
      {
        onSubmit: (e) => {
          void handleSubmit(e);
        },
        className: `space-y-6 rounded-2xl px-6 py-8 ${surface.card} ${cardClassName}`,
        children: [
          /* @__PURE__ */ jsxs14("div", { className: "sr-only", "aria-hidden": "true", children: [
            /* @__PURE__ */ jsx14("label", { htmlFor: honeypotId, children: "Company" }),
            /* @__PURE__ */ jsx14(
              "input",
              {
                id: honeypotId,
                name: "company",
                type: "text",
                autoComplete: "off",
                tabIndex: -1,
                value: honeypot,
                onChange: (event) => setHoneypot(event.target.value)
              }
            )
          ] }),
          /* @__PURE__ */ jsxs14("div", { children: [
            /* @__PURE__ */ jsx14(
              "label",
              {
                className: `mb-2 block text-sm font-semibold ${surface.label}`,
                htmlFor: "contact-name",
                children: nameLabel
              }
            ),
            /* @__PURE__ */ jsx14(
              "input",
              {
                id: "contact-name",
                name: "name",
                type: "text",
                required: true,
                placeholder: namePlaceholder,
                className: `w-full rounded-lg border-2 px-4 py-3 transition ${surface.input} ${palette.focus}`
              }
            )
          ] }),
          /* @__PURE__ */ jsxs14("div", { children: [
            /* @__PURE__ */ jsx14(
              "label",
              {
                className: `mb-2 block text-sm font-semibold ${surface.label}`,
                htmlFor: "contact-email",
                children: emailLabel
              }
            ),
            /* @__PURE__ */ jsx14(
              "input",
              {
                id: "contact-email",
                name: "email",
                type: "email",
                required: true,
                placeholder: emailPlaceholder,
                className: `w-full rounded-lg border-2 px-4 py-3 transition ${surface.input} ${palette.focus}`
              }
            )
          ] }),
          /* @__PURE__ */ jsxs14("div", { children: [
            /* @__PURE__ */ jsx14(
              "label",
              {
                className: `mb-2 block text-sm font-semibold ${surface.label}`,
                htmlFor: "contact-message",
                children: messageLabel
              }
            ),
            /* @__PURE__ */ jsx14(
              "textarea",
              {
                id: "contact-message",
                name: "message",
                required: true,
                placeholder: messagePlaceholder,
                className: `min-h-[140px] w-full resize-y rounded-lg border-2 px-4 py-3 transition ${surface.input} ${palette.focus}`
              }
            )
          ] }),
          /* @__PURE__ */ jsx14(
            Button,
            {
              type: "submit",
              tone: tone === "sage" ? "sage" : tone,
              className: "w-full justify-center text-lg",
              isLoading: submitting,
              disabled: submitting || !endpoint,
              "aria-label": submitLabel,
              children: submitLabel
            }
          ),
          privacyNotice && /* @__PURE__ */ jsx14("p", { className: `text-xs ${surface.helper}`, children: privacyNotice }),
          error && /* @__PURE__ */ jsx14("p", { className: `text-sm ${surface.error}`, role: "alert", children: error })
        ]
      }
    ),
    footer && /* @__PURE__ */ jsx14("div", { className: `text-center text-sm ${surface.helper}`, children: footer })
  ] });
}

// src/components/layout/PageShell.tsx
import { jsx as jsx15 } from "react/jsx-runtime";
function PageShell({ children, className = "" }) {
  return /* @__PURE__ */ jsx15(
    "div",
    {
      className: `flex min-h-screen flex-col bg-surface-base text-text-primary transition-colors duration-200 ${className}`,
      children: /* @__PURE__ */ jsx15(
        "main",
        {
          id: "main-content",
          className: "flex-grow px-4 sm:px-6 lg:px-8 max-w-7xl w-full mx-auto pt-8 pb-32",
          children
        }
      )
    }
  );
}

// src/components/layout/TailwindSafelist.tsx
import { jsx as jsx16, jsxs as jsxs15 } from "react/jsx-runtime";
function TailwindSafelist() {
  return /* @__PURE__ */ jsxs15("div", { className: "hidden", children: [
    /* @__PURE__ */ jsx16("div", { className: "hidden md:flex" }),
    /* @__PURE__ */ jsx16("div", { className: "flex md:hidden" }),
    /* @__PURE__ */ jsx16("div", { className: "sm:w-64 md:w-80" })
  ] });
}

// src/components/layout/ThemeToggle.tsx
import { Moon, Sun } from "lucide-react";

// src/hooks/useTheme.ts
import { useEffect as useEffect6, useState as useState6 } from "react";
function useTheme() {
  const [theme, setThemeState] = useState6("light");
  useEffect6(() => {
    const isDark = document.documentElement.classList.contains("dark");
    setThemeState(isDark ? "dark" : "light");
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemChange = (e) => {
      if (!localStorage.getItem("theme")) {
        document.documentElement.classList.toggle("dark", e.matches);
        setThemeState(e.matches ? "dark" : "light");
      }
    };
    const handleStorageChange = (e) => {
      if (e.key === "theme" && e.newValue) {
        const newTheme = e.newValue;
        document.documentElement.classList.toggle("dark", newTheme === "dark");
        setThemeState(newTheme);
      }
    };
    mediaQuery.addEventListener("change", handleSystemChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      mediaQuery.removeEventListener("change", handleSystemChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const setTheme = (newTheme) => {
    document.documentElement.classList.toggle("dark", newTheme === "dark");
    localStorage.setItem("theme", newTheme);
    setThemeState(newTheme);
  };
  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };
  return { theme, setTheme, toggleTheme };
}

// src/components/layout/ThemeToggle.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
function ThemeToggle(_props) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  return /* @__PURE__ */ jsx17(
    "button",
    {
      onClick: toggleTheme,
      className: "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-surface-raised text-text-primary border border-surface-border hover:bg-interactive-hover focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2",
      "aria-label": isDark ? "Switch to light mode" : "Switch to dark mode",
      title: isDark ? "Switch to light mode" : "Switch to dark mode",
      children: isDark ? /* @__PURE__ */ jsx17(Sun, { size: 18 }) : /* @__PURE__ */ jsx17(Moon, { size: 18 })
    }
  );
}

// src/components/ui/Typography.tsx
import { Link as RouterLink2 } from "react-router-dom";
import { jsx as jsx18, jsxs as jsxs16 } from "react/jsx-runtime";
function H1({
  children,
  className = "",
  accentColorClass,
  icon,
  ...props
}) {
  const colorClass = accentColorClass || "text-text-primary";
  return /* @__PURE__ */ jsxs16(
    "h1",
    {
      className: `text-3xl sm:text-4xl font-extrabold ${colorClass} mb-6 sm:mb-8 flex items-center gap-2 ${className}`,
      ...props,
      children: [
        icon && /* @__PURE__ */ jsx18("span", { className: "flex-shrink-0", children: icon }),
        /* @__PURE__ */ jsx18("span", { children })
      ]
    }
  );
}
function H2({
  children,
  className = "",
  accentColorClass,
  icon,
  ...props
}) {
  const colorClass = accentColorClass || "text-text-primary";
  return /* @__PURE__ */ jsxs16(
    "h2",
    {
      className: `text-2xl font-semibold ${colorClass} mb-4 flex items-center gap-2 ${className}`,
      ...props,
      children: [
        icon && /* @__PURE__ */ jsx18("span", { className: "flex-shrink-0", children: icon }),
        /* @__PURE__ */ jsx18("span", { children })
      ]
    }
  );
}
function ArticleTitle({
  children,
  className = "",
  ...props
}) {
  return /* @__PURE__ */ jsx18("h2", { className: `text-2xl lg:text-3xl font-bold text-text-primary mb-3 ${className}`, ...props, children });
}
function SubSectionTitle({
  children,
  className = "",
  icon,
  ...props
}) {
  return /* @__PURE__ */ jsxs16(
    "h3",
    {
      className: `text-xl font-semibold text-text-primary mb-3 flex items-center gap-2 ${className}`,
      ...props,
      children: [
        icon && /* @__PURE__ */ jsx18("span", { className: "flex-shrink-0", children: icon }),
        /* @__PURE__ */ jsx18("span", { children })
      ]
    }
  );
}
function CardTitle({
  children,
  className = "",
  ...props
}) {
  return /* @__PURE__ */ jsx18("h3", { className: `text-lg font-semibold text-text-primary mb-1 ${className}`, ...props, children });
}
var LEADING_CLASS_MAP = {
  relaxed: "leading-relaxed",
  normal: "leading-normal",
  loose: "leading-loose"
};
function P({
  children,
  className = "",
  leading = "relaxed",
  size = "base",
  color = "muted",
  as,
  ...props
}) {
  const Component2 = as || "p";
  const sizeClass = size === "sm" ? "text-sm" : "text-base";
  const leadingClass = LEADING_CLASS_MAP[leading] || LEADING_CLASS_MAP.relaxed;
  const colorClass = {
    primary: "text-text-primary",
    muted: "text-text-muted",
    accent: "text-text-accent"
  }[color];
  return /* @__PURE__ */ jsx18(
    Component2,
    {
      className: `${colorClass} ${sizeClass} ${leadingClass} ${className}`.trim(),
      ...props,
      children
    }
  );
}
function SmallText({
  children,
  className = "",
  color = "muted",
  leading = "normal",
  ...props
}) {
  const colorClass = {
    primary: "text-text-primary",
    muted: "text-text-muted",
    accent: "text-text-accent"
  }[color];
  return /* @__PURE__ */ jsx18(
    "p",
    {
      className: `${colorClass} text-sm ${LEADING_CLASS_MAP[leading] || LEADING_CLASS_MAP.normal} ${className}`.trim(),
      ...props,
      children
    }
  );
}
function MutedText({
  children,
  className = "",
  ...props
}) {
  return /* @__PURE__ */ jsx18("p", { className: `text-sm text-text-muted ${className}`, ...props, children });
}
function AccentLink({
  to,
  children,
  className = "",
  iconLeft,
  iconRight,
  ...props
}) {
  return /* @__PURE__ */ jsxs16(
    RouterLink2,
    {
      to,
      className: `inline-flex items-center gap-1.5 text-[var(--color-brand-accent)] hover:text-[var(--color-interactive-hover)] hover:underline transition-colors ${className}`,
      ...props,
      children: [
        iconLeft && /* @__PURE__ */ jsx18("span", { children: iconLeft }),
        children,
        iconRight && /* @__PURE__ */ jsx18("span", { children: iconRight })
      ]
    }
  );
}
function Tag({ children, className = "", colorScheme = "primary" }) {
  const colorClasses2 = {
    primary: "bg-[var(--color-brand-primary)]/20 text-[var(--color-brand-primary)] border border-[var(--color-brand-primary)]/30",
    success: "bg-[var(--color-status-success)]/20 text-[var(--color-status-success)] border border-[var(--color-status-success)]/30",
    warning: "bg-[var(--color-status-warning)]/20 text-[var(--color-status-warning)] border border-[var(--color-status-warning)]/30",
    error: "bg-[var(--color-status-error)]/20 text-[var(--color-status-error)] border border-[var(--color-status-error)]/30",
    info: "bg-[var(--color-status-info)]/20 text-[var(--color-status-info)] border border-[var(--color-status-info)]/30"
  };
  const baseStyles = "inline-flex items-center text-xs rounded-full font-medium px-2.5 py-0.5";
  return /* @__PURE__ */ jsx18(
    "span",
    {
      className: `${baseStyles} ${colorClasses2[colorScheme] || colorClasses2.primary} ${className}`,
      children
    }
  );
}
function Badge({ children, className = "", variant = "default" }) {
  const variantClasses2 = {
    default: "bg-[var(--color-brand-primary)] text-[var(--color-text-inverse)]",
    secondary: "bg-[var(--color-surface-raised)] text-[var(--color-text-primary)] border border-[var(--color-surface-border)]",
    success: "bg-[var(--color-status-success)] text-white",
    warning: "bg-[var(--color-status-warning)] text-[var(--color-text-primary)]",
    danger: "bg-[var(--color-status-error)] text-white",
    info: "bg-[var(--color-status-info)] text-white"
  };
  const baseStyles = "inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full";
  return /* @__PURE__ */ jsx18(
    "span",
    {
      className: `${baseStyles} ${variantClasses2[variant] || variantClasses2.default} ${className}`,
      children
    }
  );
}

// src/hooks/useProgressiveLoad.ts
import { useCallback as useCallback3, useEffect as useEffect7, useMemo as useMemo2, useState as useState7 } from "react";
function useProgressiveLoad(items, options = {}) {
  const { itemsPerLoad = 12, initialCount = itemsPerLoad } = options;
  const [displayCount, setDisplayCount] = useState7(initialCount);
  useEffect7(() => {
    setDisplayCount(initialCount);
  }, [items, initialCount]);
  const visibleItems = useMemo2(() => {
    return items.slice(0, displayCount);
  }, [items, displayCount]);
  const remainingCount = useMemo2(() => {
    return Math.max(0, items.length - displayCount);
  }, [items.length, displayCount]);
  const hasMore = remainingCount > 0;
  const loadMore = useCallback3(() => {
    setDisplayCount((prev) => prev + itemsPerLoad);
  }, [itemsPerLoad]);
  const reset = useCallback3(() => {
    setDisplayCount(initialCount);
  }, [initialCount]);
  return {
    visibleItems,
    remainingCount,
    loadMore,
    reset,
    hasMore,
    displayCount
  };
}

// src/hooks/useReducedMotion.ts
import { useEffect as useEffect8, useState as useState8 } from "react";
function useReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState8(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect8(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = (event) => {
      setPrefersReducedMotion(event.matches);
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);
  return prefersReducedMotion;
}

// src/utils/errorTracking.ts
var ErrorTracker = class {
  constructor() {
    __publicField(this, "errors");
    __publicField(this, "maxErrors");
    __publicField(this, "callbacks");
    this.errors = [];
    this.maxErrors = 100;
    this.callbacks = [];
  }
  /**
   * Register a callback to be called when errors are logged
   * Useful for integrating with Sentry or other services
   */
  onError(callback) {
    this.callbacks.push(callback);
    return () => {
      this.callbacks = this.callbacks.filter((cb) => cb !== callback);
    };
  }
  /**
   * Log an error
   */
  logError(error, context = {}) {
    const errorData = {
      message: error.message,
      stack: error.stack,
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      url: typeof window !== "undefined" ? window.location.href : "",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      ...context
    };
    this.errors.unshift(errorData);
    if (this.errors.length > this.maxErrors) {
      this.errors.pop();
    }
    if (typeof window !== "undefined" && window.location.hostname === "localhost") {
      console.error("[Error Tracker]", errorData);
    }
    this.callbacks.forEach((cb) => cb(error, errorData));
  }
  /**
   * Get all logged errors
   */
  getErrors() {
    return this.errors;
  }
  /**
   * Clear error log
   */
  clearErrors() {
    this.errors = [];
  }
  /**
   * Get error statistics
   */
  getStats() {
    const errorsByType = {};
    const errorsByUrl = {};
    this.errors.forEach((error) => {
      const type = error.message || "Unknown";
      errorsByType[type] = (errorsByType[type] || 0) + 1;
      const url = error.url || "Unknown";
      errorsByUrl[url] = (errorsByUrl[url] || 0) + 1;
    });
    return {
      total: this.errors.length,
      errorsByType,
      errorsByUrl,
      mostRecent: this.errors[0]
    };
  }
};
var errorTracker = new ErrorTracker();
function setupErrorTracking() {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (event) => {
    const error = event.error instanceof Error ? event.error : new Error(event.message);
    errorTracker.logError(error, {
      type: "unhandled",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    event.preventDefault();
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    errorTracker.logError(error, {
      type: "unhandledRejection"
    });
    if (typeof window !== "undefined" && window.location.hostname === "localhost") {
      console.error("Unhandled promise rejection:", error);
    }
  });
  if ("PerformanceObserver" in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 3e3) {
            errorTracker.logError(new Error("Slow operation detected"), {
              type: "performance",
              name: entry.name,
              duration: entry.duration,
              entryType: entry.entryType
            });
          }
        }
      });
      observer.observe({ entryTypes: ["measure", "navigation"] });
    } catch {
    }
  }
}
var withErrorTracking = (fn, context) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      errorTracker.logError(error, { context, args });
      throw error;
    }
  };
};

// src/utils/initTheme.ts
function initTheme() {
  const stored = localStorage.getItem("theme");
  const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const theme = stored || (systemDark ? "dark" : "light");
  document.documentElement.classList.toggle("dark", theme === "dark");
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const handleChange = (e) => {
    if (!localStorage.getItem("theme")) {
      document.documentElement.classList.toggle("dark", e.matches);
    }
  };
  mediaQuery.addEventListener("change", handleChange);
}

// src/utils/validation.ts
function validateEmail(email) {
  if (!email || email.trim().length === 0) {
    return { isValid: false, error: "Email is required" };
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: "Please enter a valid email address" };
  }
  return { isValid: true };
}
function validatePhone(phone) {
  if (!phone || phone.trim().length === 0) {
    return { isValid: false, error: "Phone number is required" };
  }
  const digitsOnly = phone.replace(/\D/g, "");
  if (digitsOnly.length < 10) {
    return { isValid: false, error: "Phone number must be at least 10 digits" };
  }
  if (digitsOnly.length > 15) {
    return { isValid: false, error: "Phone number is too long" };
  }
  return { isValid: true };
}
function validateName(name) {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: "Name is required" };
  }
  if (name.trim().length < 2) {
    return { isValid: false, error: "Name must be at least 2 characters" };
  }
  if (name.length > 100) {
    return { isValid: false, error: "Name must not exceed 100 characters" };
  }
  const nameRegex = /^[a-zA-Z\s'-]+$/;
  if (!nameRegex.test(name)) {
    return {
      isValid: false,
      error: "Name can only contain letters, spaces, hyphens, and apostrophes"
    };
  }
  return { isValid: true };
}
function validateDateOfBirth(dob) {
  if (!dob || dob.trim().length === 0) {
    return { isValid: false, error: "Date of birth is required" };
  }
  const birthDate = new Date(dob);
  const today = /* @__PURE__ */ new Date();
  if (isNaN(birthDate.getTime())) {
    return { isValid: false, error: "Please enter a valid date" };
  }
  if (birthDate > today) {
    return { isValid: false, error: "Date of birth cannot be in the future" };
  }
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || monthDiff === 0 && today.getDate() < birthDate.getDate()) {
    age--;
  }
  if (age < 18) {
    return { isValid: false, error: "You must be at least 18 years old" };
  }
  if (age > 120) {
    return { isValid: false, error: "Please enter a valid date of birth" };
  }
  return { isValid: true };
}
function validateAddress(address) {
  if (!address || address.trim().length === 0) {
    return { isValid: false, error: "Address is required" };
  }
  if (address.trim().length < 5) {
    return { isValid: false, error: "Address must be at least 5 characters" };
  }
  if (address.length > 200) {
    return { isValid: false, error: "Address must not exceed 200 characters" };
  }
  return { isValid: true };
}
function validateCity(city) {
  if (!city || city.trim().length === 0) {
    return { isValid: false, error: "City is required" };
  }
  if (city.trim().length < 2) {
    return { isValid: false, error: "City must be at least 2 characters" };
  }
  if (city.length > 50) {
    return { isValid: false, error: "City must not exceed 50 characters" };
  }
  return { isValid: true };
}
function validateState(state) {
  if (!state || state.trim().length === 0) {
    return { isValid: false, error: "State is required" };
  }
  if (state.length !== 2) {
    return { isValid: false, error: "State must be 2 letters (e.g., CA, NY)" };
  }
  const stateRegex = /^[A-Z]{2}$/;
  if (!stateRegex.test(state.toUpperCase())) {
    return { isValid: false, error: "Please enter a valid 2-letter state code" };
  }
  return { isValid: true };
}
function validateZipCode(zip) {
  if (!zip || zip.trim().length === 0) {
    return { isValid: false, error: "ZIP code is required" };
  }
  const zipRegex = /^\d{5}(-\d{4})?$/;
  if (!zipRegex.test(zip)) {
    return { isValid: false, error: "Please enter a valid ZIP code (e.g., 12345 or 12345-6789)" };
  }
  return { isValid: true };
}
function validateMessage(message, minLength = 10, maxLength = 5e3) {
  if (!message || message.trim().length === 0) {
    return { isValid: false, error: "Message is required" };
  }
  if (message.trim().length < minLength) {
    return { isValid: false, error: `Message must be at least ${minLength} characters` };
  }
  if (message.length > maxLength) {
    return { isValid: false, error: `Message must not exceed ${maxLength} characters` };
  }
  return { isValid: true };
}
function sanitizeInput(input) {
  if (typeof input !== "string") {
    return "";
  }
  return input.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#x27;").replace(/\//g, "&#x2F;").trim();
}
function validateRequired(value, fieldName = "Field") {
  if (!value || value.trim().length === 0) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  return { isValid: true };
}
function validateLength(value, min, max, fieldName = "Field") {
  const length = value.trim().length;
  if (length < min) {
    return { isValid: false, error: `${fieldName} must be at least ${min} characters` };
  }
  if (length > max) {
    return { isValid: false, error: `${fieldName} must be no more than ${max} characters` };
  }
  return { isValid: true };
}
function validateUrl(url, fieldName = "URL") {
  if (!url || url.trim().length === 0) {
    return { isValid: false, error: `${fieldName} is not configured` };
  }
  try {
    const parsedUrl = new URL(url);
    if (!parsedUrl.protocol.startsWith("http")) {
      return { isValid: false, error: `Invalid ${fieldName} protocol` };
    }
    return { isValid: true };
  } catch {
    return { isValid: false, error: `Invalid ${fieldName} format` };
  }
}
export {
  AccentLink,
  ActiveFilterBadges,
  AggregateRating,
  ArticleTitle,
  Badge,
  Button,
  CardTitle,
  ContactForm,
  ContentCard,
  ContentSearch,
  ContentSort,
  EmptyState,
  ErrorBoundary,
  ErrorCard,
  ErrorMessage,
  ErrorPage,
  H1,
  H2,
  LoadMoreButton,
  LoadingCard,
  LoadingPage,
  LoadingSpinner,
  MutedText,
  P,
  PageShell,
  SiteSearch,
  Skeleton,
  SkeletonCard,
  SkeletonText,
  SmallText,
  StarRating,
  StatCard,
  SubSectionTitle,
  Tag,
  TailwindSafelist,
  ThemeToggle,
  errorTracker,
  initTheme,
  sanitizeInput,
  setupErrorTracking,
  useProgressiveLoad,
  useReducedMotion,
  useTheme as useSimpleTheme,
  validateAddress,
  validateCity,
  validateDateOfBirth,
  validateEmail,
  validateLength,
  validateMessage,
  validateName,
  validatePhone,
  validateRequired,
  validateState,
  validateUrl,
  validateZipCode,
  withErrorTracking
};
//# sourceMappingURL=index.js.map